export const INGESTION_STATUSES = [
  'CREATED',
  'AWAITING_UPLOAD',
  'STARTING',
  'RETRIEVING_FILES',
  'PARSING_CSV',
  'EXTRACTING_IMAGES',
  'GENERATING_ASSETS',
  'DONE',
  'FAILED',
] as const;

export type IngestionStatus = (typeof INGESTION_STATUSES)[number];

export interface IngestionJobStatus {
  id: string;
  status: IngestionStatus;
  assetsCompleted: number;
  assetsTotal: number;
  updatedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isNonNegativeSafeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

export function isIngestionStatus(value: unknown): value is IngestionStatus {
  return typeof value === 'string' && INGESTION_STATUSES.includes(value as IngestionStatus);
}

export function isIngestionJobStatus(value: unknown): value is IngestionJobStatus {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    isIngestionStatus(value.status) &&
    isNonNegativeSafeInteger(value.assetsCompleted) &&
    isNonNegativeSafeInteger(value.assetsTotal) &&
    value.assetsCompleted <= value.assetsTotal &&
    typeof value.updatedAt === 'string' &&
    isNullableString(value.completedAt) &&
    isNullableString(value.errorMessage)
  );
}

export function deriveWebSocketUrl(
  configuredUrl: string | undefined,
  configuredApiUrl: string | undefined,
): string {
  const explicitUrl = configuredUrl?.trim();
  if (explicitUrl) {
    return explicitUrl;
  }

  const apiBaseUrl = configuredApiUrl?.trim() || 'http://localhost:3000/api';

  try {
    const apiUrl = new URL(apiBaseUrl);
    apiUrl.protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    apiUrl.pathname = apiUrl.pathname.replace(/\/api\/?$/, '') + '/ws/admin/ingestion';
    apiUrl.search = '';
    apiUrl.hash = '';
    return apiUrl.toString();
  } catch {
    return 'ws://localhost:3000/ws/admin/ingestion';
  }
}

export function isTerminalIngestionStatus(status: IngestionStatus): boolean {
  return status === 'DONE' || status === 'FAILED';
}
